import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, LayoutDashboard, Globe, HelpCircle, Star, Briefcase, Settings as SettingsIcon, Eye, Pencil, EyeOff, Check, ChevronRight, ChevronDown, ImageIcon, Info, List, Type, Eye as EyeIcon, Share2, MousePointer2, Users, Menu, Mail, FileText, MessageSquare, FormInput, Phone } from "lucide-react";
import { getRoles, updateRole } from "../../lib/api";
import { MENU_CONTROL_TABS as allTabs, MODES, MODE_CONFIG } from "../../lib/tabs";

function flattenTabs(tabs) {
  const result = [];
  for (const t of tabs) {
    result.push(t.uid);
    if (t.children) {
      for (const c of t.children) {
        result.push(c.uid);
        if (c.children) {
          for (const gc of c.children) result.push(gc.uid);
        }
      }
    }
  }
  return result;
}

export default function AdminMenuControl({ readOnly = false, userRole }) {
  const isDevUser = userRole === "developer";
  const effectiveReadOnly = readOnly || !isDevUser;
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const rolesRes = await getRoles();
      const rolesList = Array.isArray(rolesRes) ? rolesRes : [];
      setRoles(rolesList);

      const permMap = {};
      rolesList.forEach(r => {
        const def = {};
        flattenTabs(allTabs).forEach(t => { def[t] = r.name === "developer" ? "edit" : "off"; });
        permMap[r.uid] = def;
      });

      rolesList.forEach(r => {
        if (r.name === "developer") return;
        try {
          const raw = r.menu_permissions ? JSON.parse(r.menu_permissions) : {};
          if (Array.isArray(raw)) {
            raw.forEach(t => { permMap[r.uid][t] = "edit"; });
          } else {
            flattenTabs(allTabs).forEach(t => { if (raw[t]) permMap[r.uid][t] = raw[t]; });
          }
        } catch {}
      });

      setPermissions(permMap);
    } catch {
      toast.error("Erro ao carregar permissões");
    } finally {
      setLoading(false);
    }
  }

  async function savePerms(roleUid, tabs) {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    setSaving(roleUid);
    setPermissions(prev => ({ ...prev, [roleUid]: tabs }));

    try {
      await updateRole({ uid: roleUid, menu_permissions: JSON.stringify(tabs) });
      toast.success("Permissões atualizadas");
    } catch {
      toast.error("Erro ao salvar");
      load();
    } finally {
      setSaving(null);
    }
  }

  function setMode(roleUid, tabUid, mode) {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    const current = permissions[roleUid] || {};
    const updated = { ...current, [tabUid]: mode };
    savePerms(roleUid, updated);
  }

  function findTab(tabs, uid) {
    for (const t of tabs) {
      if (t.uid === uid) return t;
      if (t.children) { const found = findTab(t.children, uid); if (found) return found; }
    }
    return null;
  }

  function setGroupMode(roleUid, groupUid, mode) {
    if (effectiveReadOnly) { toast.warning("Sem permissão para editar."); return; }
    const group = findTab(allTabs, groupUid);
    if (!group || !group.children) return;
    const current = { ...(permissions[roleUid] || {}) };
    current[groupUid] = mode;
    group.children.forEach(c => { current[c.uid] = mode; });
    savePerms(roleUid, current);
  }

  function getSummary(roleUid) {
    const perms = permissions[roleUid] || {};
    const counts = { off: 0, readonly: 0, edit: 0 };
    flattenTabs(allTabs).forEach(t => { counts[perms[t] || "off"]++; });
    return counts;
  }

  function toggleGroup(uid) {
    setExpandedGroups(prev => ({ ...prev, [uid]: !prev[uid] }));
  }

  return (
    <div data-id="admin-menu-control" className="space-y-8">
      <div>
        <h2 className="font-serif text-3xl text-softblack">Controle de Menu</h2>
        <p className="text-warmgray text-sm mt-1">Gerencie acesso e permissões de cada perfil (role)</p>
      </div>

      <div className="flex gap-6 text-xs text-warmgray bg-white border border-fendi rounded-lg px-5 py-3">
        {MODES.map(m => {
          const cfg = MODE_CONFIG[m];
          return (
            <span key={m} className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${cfg.bg} ${cfg.color}`}>
              <cfg.Icon size={13} />
              <span className="font-medium">{cfg.label}</span>
            </span>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-champagne" size={24} /></div>
      ) : (
        <div className="space-y-4">
          {roles.map(r => {
            const isExpanded = selectedRole === r.uid;
            const summary = getSummary(r.uid);
            const isDev = r.name === "developer";

            return (
              <div key={r.uid} className="bg-white border border-fendi rounded-xl overflow-hidden">
                <button
                  onClick={() => isDev ? null : setSelectedRole(isExpanded ? null : r.uid)}
                  className={`w-full flex items-center justify-between px-6 py-4 transition-colors ${isDev ? "cursor-default" : "hover:bg-offwhite/50"}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-2.5 h-2.5 rounded-full ${r.color || "#C8A97E"}`} style={r.color ? { backgroundColor: r.color } : {}} />
                    <div className="text-left">
                      <p className="text-sm text-softblack font-medium">{r.label || r.name}</p>
                      <p className="text-xs text-warmgray font-mono">{r.name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      {MODES.map(m => {
                        const cfg = MODE_CONFIG[m];
                        const count = summary[m];
                        if (count === 0) return null;
                        return (
                          <span key={m} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${cfg.bg} ${cfg.color}`}>
                            <cfg.Icon size={10} />
                            {count}
                          </span>
                        );
                      })}
                    </div>
                    {!isDev && (
                      <svg className={`w-4 h-4 text-warmgray transition-transform ${isExpanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </div>
                </button>

                {isExpanded && !isDev && (
                  <div className="border-t border-fendi/30">
                    <div className="px-6 py-3 bg-fendi/5 flex flex-col sm:flex-row sm:items-center gap-3">
                      <span className="text-[10px] uppercase tracking-widest text-warmgray font-medium">Aplicar a todos</span>
                      <div className="flex gap-2 flex-wrap">
                        {MODES.map(m => {
                          const cfg = MODE_CONFIG[m];
                          return (
                            <button
                              key={m}
                              onClick={() => {
                                const updated = {};
                                flattenTabs(allTabs).forEach(t => { updated[t] = m; });
                                savePerms(r.uid, updated);
                              }}
                              disabled={saving === r.uid}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-medium uppercase tracking-wider border transition-all ${cfg.bg} ${cfg.color} ${cfg.border} hover:ring-2 ${cfg.ring} disabled:opacity-50`}
                            >
                              <cfg.Icon size={11} />
                              {cfg.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="px-6 py-4 space-y-2">
                      {allTabs.map(t => {
                        const perms = permissions[r.uid] || {};
                        const hasChildren = t.children && t.children.length > 0;
                        const isGroupExpanded = expandedGroups[t.uid];
                        const parentMode = perms[t.uid] || "off";

                        return (
                          <div key={t.uid}>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-2 px-3 rounded-lg hover:bg-fendi/5 transition-colors">
                              {hasChildren && (
                                <button onClick={() => toggleGroup(t.uid)} className="p-0.5 text-warmgray hover:text-softblack">
                                  {isGroupExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                </button>
                              )}
                              {!hasChildren && <div className="w-5" />}
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <t.Icon size={15} className="text-warmgray shrink-0" />
                                <span className="text-sm text-softblack font-medium truncate">{t.label}</span>
                              </div>

                              <div className="flex gap-1 shrink-0">
                                {MODES.map(m => {
                                  const cfg = MODE_CONFIG[m];
                                  const isActive = parentMode === m;
                                  return (
                                    <button
                                      key={m}
                                      onClick={() => hasChildren ? setGroupMode(r.uid, t.uid, m) : setMode(r.uid, t.uid, m)}
                                      disabled={saving === r.uid}
                                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-all disabled:opacity-50 ${
                                        isActive
                                          ? `${cfg.bg} ${cfg.color} ${cfg.border} ring-2 ${cfg.ring}`
                                          : `bg-white text-gray-400 border-gray-100 hover:border-gray-200 hover:text-gray-500`
                                      }`}
                                    >
                                      <cfg.Icon size={12} />
                                      {cfg.label}
                                      {isActive && <Check size={11} className="ml-0.5" />}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {hasChildren && isGroupExpanded && (
                              <div className="ml-8 mt-1 space-y-1 border-l-2 border-fendi/20 pl-4">
                                {t.children.map(c => {
                                  const childHasChildren = c.children && c.children.length > 0;
                                  const isChildExpanded = expandedGroups[c.uid];
                                  const childMode = perms[c.uid] || "off";
                                  return (
                                    <div key={c.uid}>
                                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 py-1.5 px-3 rounded-lg hover:bg-fendi/5 transition-colors">
                                        {childHasChildren && (
                                          <button onClick={() => toggleGroup(c.uid)} className="p-0.5 text-warmgray hover:text-softblack shrink-0">
                                            {isChildExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                          </button>
                                        )}
                                        {!childHasChildren && <div className="hidden sm:block w-5" />}
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                          <c.Icon size={13} className="text-warmgray/60 shrink-0" />
                                          <span className="text-sm text-warmgray truncate">{c.label}</span>
                                        </div>

                                        <div className="flex gap-1 shrink-0">
                                          {MODES.map(m => {
                                            const cfg = MODE_CONFIG[m];
                                            const isActive = childMode === m;
                                            return (
                                              <button
                                                key={m}
                                                onClick={() => childHasChildren ? setGroupMode(r.uid, c.uid, m) : setMode(r.uid, c.uid, m)}
                                                disabled={saving === r.uid}
                                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border transition-all disabled:opacity-50 ${
                                                  isActive
                                                    ? `${cfg.bg} ${cfg.color} ${cfg.border} ring-2 ${cfg.ring}`
                                                    : `bg-white text-gray-400 border-gray-100 hover:border-gray-200 hover:text-gray-500`
                                                }`}
                                              >
                                                <cfg.Icon size={10} />
                                                {cfg.label}
                                                {isActive && <Check size={9} className="ml-0.5" />}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>

                                      {childHasChildren && isChildExpanded && (
                                        <div className="ml-8 mt-1 space-y-1 border-l-2 border-fendi/20 pl-4">
                                          {c.children.map(gc => {
                                            const gcMode = perms[gc.uid] || "off";
                                            return (
                                              <div key={gc.uid} className="flex flex-col sm:flex-row sm:items-center gap-2 py-1.5 px-3 rounded-lg hover:bg-fendi/5 transition-colors">
                                                <div className="hidden sm:block w-5" />
                                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                                  <gc.Icon size={11} className="text-warmgray/40 shrink-0" />
                                                  <span className="text-xs text-warmgray/80 truncate">{gc.label}</span>
                                                </div>
                                                <div className="flex gap-1 shrink-0">
                                                  {MODES.map(m => {
                                                    const cfg = MODE_CONFIG[m];
                                                    const isActive = gcMode === m;
                                                    return (
                                                      <button
                                                        key={m}
                                                        onClick={() => setMode(r.uid, gc.uid, m)}
                                                        disabled={saving === r.uid}
                                                        className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border transition-all disabled:opacity-50 ${
                                                          isActive
                                                            ? `${cfg.bg} ${cfg.color} ${cfg.border} ring-2 ${cfg.ring}`
                                                            : `bg-white text-gray-400 border-gray-100 hover:border-gray-200 hover:text-gray-500`
                                                        }`}
                                                      >
                                                        <cfg.Icon size={9} />
                                                        {cfg.label}
                                                        {isActive && <Check size={8} className="ml-0.5" />}
                                                      </button>
                                                    );
                                                  })}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isDev && isExpanded && (
                  <div className="border-t border-fendi/30 px-6 py-8 text-center">
                    <p className="text-sm text-warmgray">Developer tem acesso completo a todos os módulos.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}